import { template } from 'lodash';
import months from '../utils/months';
import domains from '../containers/Domains';
import contextMakePost from './Context';
import {
  conformCustomCampaign,
  accountIdsForIgnoringMonth
} from '../utils/campaignData';
import { getLocalStorageDataOrFetch } from './fbCallManager';

const endpoint = domains.fbEndpoint;

const createCampaignParams = (campaign, campaignData) => {
  const campaignNameParts = campaignData.name.split('_');
  const year = campaignNameParts[0];
  const month = campaignNameParts[1];
  let newYear = year;
  let newMonth = month;
  let newMonthInfo;

  if (month === 'Dec') {
    newYear += 1;
    newMonth = 'Jan';
    newMonthInfo = months.filter((val) => val.shortName === newMonth)[0];
  } else {
    let monthNumber = months.filter((val) => val.shortName === month)[0]
      .monthNumber;
    monthNumber += 1;
    newMonthInfo = months.filter((val) => val.monthNumber === monthNumber)[0];
    newMonth = newMonthInfo.shortName;
  }

  const startTime = `${newYear}'-'${newMonthInfo.monthNumber}'-1 '${newMonthInfo.startHour}':00:00-07:00'`;
  const endTime = `${newYear}'-'${newMonthInfo.monthNumber}'-'${newMonthInfo.endDay}' '${newMonthInfo.endHour}':00:00-07:00'`;
  const body = `ad_campaign_id=${campaign}&start_time=${startTime}&end_time=${endTime}&deep_copy=true&status_option=PAUSED&rename_options={"rename_strategy":"DEEP_RENAME"}`;

  const params = {
    method: 'POST',
    relative_url: `${campaign}/copies`,
    name: `copy-${campaign}`,
    body
  };

  return params;
};

export const updateCampaign = async (data, token) => {
  const { name, special_ad_categories: specialAdCategory } = data;
  const parameters = {
    access_token: token,
    name
  };
  if (typeof specialAdCategory === 'string') {
    parameters.special_ad_categories = specialAdCategory;
  }

  const id = data.id;
  const result = await contextMakePost(parameters, id);
  return result;
};

export const copyCampaign = async (campaign, token) => {
  const parameters = {
    access_token: token,
    deep_copy: false
  };

  const id = `${campaign}/copies`;
  const result = await contextMakePost(parameters, id);
  return result.json();
};

export const copyCampaigns = async (campaigns, token) => {
  const parameters = {
    access_token: token,
    asyncbatch: []
  };

  const allParameters = await Promise.all(
    campaigns.map(async (campaign) => {
      const campaignData = await getCampaign(campaign, token);
      return Promise.resolve(createCampaignParams(campaign, campaignData));
    })
  );

  parameters.asyncbatch = allParameters;
  const result = await contextMakePost(parameters);
  return result.json();
};

export const getCampaign = async (campaign, token) => {
  const parameters = {
    access_token: token,
    campaign_id: campaign,
    fields:
      'id,name,objective,special_ad_categories,created_time,start_time,stop_time,adlabels,adsets{name,ads{id,name,creative{id}}}'
    // original fields: 'id,name,objective,created_time,adlabels,adsets{name,ads{id,name,creative{object_story_spec}}},ads'
  };

  const query = template(
    `${endpoint}<%= campaign_id%>?fields=<%= fields%>&access_token=<%= access_token%>`
  );

  const jsonResult = await getLocalStorageDataOrFetch(query(parameters));

  return jsonResult;
};

export const getCampaignWithPromotedObjects = async (campaign, token) => {
  const parameters = {
    access_token: token,
    campaign_id: campaign,
    fields:
      'id,account_id,name,objective,special_ad_categories,created_time,start_time,adlabels,adsets{name,promoted_object,ads{id,name,creative{object_story_spec}}}'
  };

  const query = template(
    `${endpoint}<%= campaign_id%>?fields=<%= fields%>&access_token=<%= access_token%>`
  );

  const jsonResult = await getLocalStorageDataOrFetch(query(parameters));

  if (!accountIdsForIgnoringMonth.includes(jsonResult.account_id))
    return jsonResult;

  return conformCustomCampaign(jsonResult);
};

export const getCampaigns = async (accountId, token, screen) => {
  let parameters = {};
  let query = '';

  if (screen) {
    parameters = {
      access_token: token,
      account_Id: accountId,
      fields: 'name,start_time,adlabels,objective,special_ad_categories'
    };
    query = template(
      `${endpoint}act_<%= account_Id%>/campaigns?fields=<%= fields%>&limit=50&access_token=<%= access_token%>`
    );
  } else {
    parameters = {
      access_token: token,
      account_Id: accountId,
      fields:
        'name,objective,start_time,adlabels,special_ad_categories,p{id,name,promoted_object,ads{creative{object_story_spec}}}&limit=100'
    };
    query = template(
      `${endpoint}act_<%= account_Id%>/campaigns?fields=<%= fields%>&limit=100&access_token=<%= access_token%>`
    );
  }
  const jsonResult = await getLocalStorageDataOrFetch(query(parameters));

  if (!accountIdsForIgnoringMonth.includes(accountId)) return jsonResult;

  return {
    ...jsonResult,
    data: jsonResult.data.map(conformCustomCampaign)
  };
};

export const getCampaignsWithoutAdsets = async (accountId, token) => {
  let parameters = {};
  let query = '';
  parameters = {
    access_token: token,
    account_Id: accountId,
    fields: 'name,start_time,adlabels,objective,special_ad_categories'
  };
  query = template(
    `${endpoint}act_<%= account_Id%>/campaigns?fields=<%= fields%>&limit=100&access_token=<%= access_token%>`
  );

  const jsonResult = await getLocalStorageDataOrFetch(query(parameters));

  if (!accountIdsForIgnoringMonth.includes(accountId)) return jsonResult;

  return {
    ...jsonResult,
    data: jsonResult.data.map(conformCustomCampaign)
  };
};

export const createCampaign = async (accountId, params) => {
  const parameters = { ...params };
  switch (parameters.objective) {
    case 'LINK_CLICKS':
      parameters.objective = 'OUTCOME_TRAFFIC';
      break;
    case 'VIDEO_VIEWS':
      parameters.objective = 'OUTCOME_ENGAGEMENT';
      break;
    case 'CONVERSIONS':
      parameters.objective = 'OUTCOME_ENGAGEMENT';
      break;
    case 'PRODUCT_CATALOG_SALES':
      parameters.objective = 'OUTCOME_SALES';
      break;
    default:
      break;
  }
  // console.log("accountId", accountId)
  // console.log("parameters", parameters)
  const id = `act_${accountId}/campaigns`;
  const result = await contextMakePost(parameters, id);
  return result.json();
};



// WEBPACK FOOTER //
// ./src/fbApi/Campaign.js